import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TablePagination } from '@mui/material';
import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import alertImage from 'assets/img/alert.svg';

import {
  CURRENCY_FORMATTER,
  PAGE_INITIAL_VALUE,
  PRODUCT_INVALID_VALUE,
  PRODUCT_ROWS_PER_PAGE_OPTION,
} from 'common/constants';
import {
  ACTIVE_PRODUCT_MESSAGE,
  DEACTIVE_PRODUCT_MESSAGE,
} from 'common/constants/changeProductAvailabilityMessage.constants';
import { formatCurrency } from 'common/constants/currency';
import { theme } from 'v3/styles/global-styles';

import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  groupProductsHotUpdate,
  setGroupProducts,
  setGroupProductSelected,
  setLimit,
  setPage,
} from 'v3/data/store/product/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';

import { IGroupProduct } from 'v3/data/store/product/types';
import { listGroupProducts, updateGroupProduct } from 'v3/services/api';

import { SmartphoneFilter } from 'v3/shared/components/Filters/SmartphoneFilter';
import { ChangeValueModal } from 'v3/shared/components/Modal/ChangePriceModal';
import { ConfirmationModal } from 'v3/shared/components/Modal/ConfirmationModal';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { SwitchCommon } from 'v3/shared/components/Switch';
import { PaperStyled, TableMobilePaginationStyles } from 'v3/shared/components/Table/styles';
import { CommonTemplate } from 'v3/shared/templates/CommonTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';

import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { ProductMobileTableForGroup } from 'v3/shared/components/MobileTable/ProductMobileTableForGroup';
import { LabelSmall } from 'v3/shared/typography/LabelSmall';
import {
  Container,
  Content,
  DeviceImageContainer,
  DeviceImg,
  Header,
  SelectPropsStyles,
  TableCellGroup,
  TableCellInColumn,
  TableCellInRow,
  TableCellStyled,
  TableContainerStyled,
  TablePaginationStyles,
  TableRowStyled,
  UnsupportedValueCard,
} from '../styles';

export const GroupAdminProductsListPage = () => {
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoints();
  const { limit, page, available, model } = useStoreSelector((store) => store.products.productsFilter);
  const { storeUserId } = useStoreSelector((store) => store.store.storeSelected);
  const { groupProductSelected } = useStoreSelector((store) => store.products);
  const { count } = useStoreSelector((store) => store.products.groupProducts);
  const { groupProducts } = useStoreSelector((store) => store.products);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [changePriceModalOpen, setChangePriceModalOpen] = useState(false);
  const [subtitle, setSubtitle] = useState('');

  const fetchProductList = useCallback(async () => {
    try {
      const response = await listGroupProducts({ page, limit, available, productname: model });
      setGroupProducts(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de produtos', error);
    }
  }, [page, limit, available, model]);

  const handleSwitchChange = (product: IGroupProduct, event: React.ChangeEvent<HTMLInputElement>) => {
    const switchNewValue = Boolean(event.target.checked);
    setGroupProductSelected({ ...product, availability: switchNewValue });
    const currentSubtitle = switchNewValue ? ACTIVE_PRODUCT_MESSAGE : DEACTIVE_PRODUCT_MESSAGE;
    setSubtitle(currentSubtitle);
    setConfirmationModalOpen(true);
  };

  const changePriceHandler = async (storePrice: number) => {
    const newPrice = Number(storePrice) / CURRENCY_FORMATTER;
    await updateGroupProduct(groupProductSelected.sku.id, {
      price: newPrice,
      storeUserId,
    });
    groupProductsHotUpdate({ ...groupProductSelected, price: newPrice });
  };
  const handleOpenEditPriceModal = (product: IGroupProduct) => {
    setGroupProductSelected(product);
    setChangePriceModalOpen(true);
  };

  const handleConfirm = async () => {
    try {
      await updateGroupProduct(groupProductSelected.sku.id, {
        availability: groupProductSelected.availability,
        storeUserId,
      });

      groupProductsHotUpdate(groupProductSelected);

      groupProductSelected.availability
        ? showSnackbar('O produto está ativo no catálogo', 'success', true)
        : showSnackbar('O produto foi desativado do catálogo', 'success', true);
    } catch (error) {
      showErrorDialog('Erro ao editar disponibilidade do produto.', error);
    } finally {
      setConfirmationModalOpen(false);
    }
  };

  const shouldDisableAvailabilityButton = (product: IGroupProduct) => {
    const isUnavailableWithInvalidPrice = !product.availability && product.price <= PRODUCT_INVALID_VALUE;
    const isPriceExceedsMaxLimit = product.price > product.sku.maxPrice;
    return isUnavailableWithInvalidPrice || isPriceExceedsMaxLimit;
  };

  useEffect(() => {
    fetchProductList();
  }, [page, limit, available, model]);

  return (
    <CommonTemplate>
      <Container>
        <Content>
          <Header>
            <HeadlineMedium>Lista de produtos</HeadlineMedium>
          </Header>
          <SmartphoneFilter />
          {isDesktop ? (
            <PaperStyled>
              <TableContainerStyled>
                {groupProducts.items.isEmpty ? (
                  <NoItemsFound />
                ) : (
                  <TableMui stickyHeader aria-label="sticky tables">
                    <TableHead>
                      <TableRow>
                        <TableCellStyled>Modelo</TableCellStyled>
                        <TableCellStyled>Preço</TableCellStyled>
                        <TableCellStyled align="center">Mostrar no catálogo</TableCellStyled>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupProducts?.items?.map((product: IGroupProduct) => (
                        <TableRowStyled key={product.sku.id}>
                          <TableCellStyled sx={{ backgroundColor: 'transparent' }}>
                            <TableCellInRow>
                              <DeviceImageContainer>
                                <DeviceImg src={product.sku.image.marketingUrl} alt={product.sku.product.name} />
                              </DeviceImageContainer>
                              <TableCellInColumn>
                                <SubtitleMedium>{product.sku.product.name}</SubtitleMedium>
                                <SubtitleMedium color={theme.palette.secondary.main}>
                                  {` ${product.sku.storage}GB`}
                                </SubtitleMedium>
                              </TableCellInColumn>
                            </TableCellInRow>
                          </TableCellStyled>

                          <TableCellGroup>
                            <TableCellInRow>
                              <SubtitleMedium>{formatCurrency(product.price)}</SubtitleMedium>
                              <EditOutlinedIcon onClick={() => handleOpenEditPriceModal(product)} />
                            </TableCellInRow>
                            {product.price <= PRODUCT_INVALID_VALUE && (
                              <UnsupportedValueCard>
                                <LabelSmall>Necessário informar o preço</LabelSmall>
                              </UnsupportedValueCard>
                            )}
                            {product.price > product.sku.maxPrice && (
                              <UnsupportedValueCard>
                                <LabelSmall>
                                  Valor máximo do produto deve ser{' '}
                                  {formatCurrency(parseFloat(product.sku.maxPrice.toFixed(2)))}
                                </LabelSmall>
                              </UnsupportedValueCard>
                            )}
                          </TableCellGroup>

                          <TableCellGroup align="center">
                            <SwitchCommon
                              disabled={shouldDisableAvailabilityButton(product)}
                              checked={product.availability}
                              onChange={(event) => {
                                handleSwitchChange(product, event);
                              }}
                            />
                          </TableCellGroup>
                        </TableRowStyled>
                      ))}
                    </TableBody>
                  </TableMui>
                )}
              </TableContainerStyled>
              <TablePagination
                rowsPerPageOptions={PRODUCT_ROWS_PER_PAGE_OPTION}
                component="div"
                sx={TablePaginationStyles}
                slotProps={{
                  select: {
                    ...SelectPropsStyles,
                    IconComponent: KeyboardArrowDownIcon,
                  },
                }}
                count={count}
                rowsPerPage={limit}
                page={page}
                onPageChange={(event, value) => {
                  setPage(value);
                }}
                onRowsPerPageChange={(event) => {
                  setPage(PAGE_INITIAL_VALUE);
                  setLimit(Number(event.target.value));
                }}
              />
            </PaperStyled>
          ) : (
            <Fragment>
              <ProductMobileTableForGroup
                shouldDisableAvailability={shouldDisableAvailabilityButton}
                items={groupProducts.items}
              ></ProductMobileTableForGroup>
              <TablePagination
                rowsPerPageOptions={PRODUCT_ROWS_PER_PAGE_OPTION}
                component="div"
                sx={TableMobilePaginationStyles}
                count={count}
                slotProps={{
                  select: {
                    ...SelectPropsStyles,
                    IconComponent: KeyboardArrowDownIcon,
                  },
                }}
                rowsPerPage={limit}
                page={page}
                labelRowsPerPage="Cards por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                onPageChange={(event, value) => {
                  setPage(value);
                }}
                onRowsPerPageChange={(event) => {
                  setPage(PAGE_INITIAL_VALUE);
                  setLimit(Number(event.target.value));
                }}
              />
            </Fragment>
          )}

          <ChangeValueModal
            modal={{
              initialPrice: groupProductSelected.price,
              active: changePriceModalOpen,
              title: 'Editar Preço',
              activeButton: true,
              cancelButton: 'Cancelar',
              saveButton: 'Salvar',
              maxPrice: groupProductSelected.sku.maxPrice,
            }}
            onClose={() => setChangePriceModalOpen(false)}
            onConfirm={changePriceHandler}
          />

          <ConfirmationModal
            modal={{
              active: confirmationModalOpen,
              title: 'Você tem certeza?',
              subtitle: subtitle,
              image: alertImage,
              cancelButtonText: 'Cancelar',
              confirmButtonText: 'Sim, desejo continuar',
            }}
            onClose={() => setConfirmationModalOpen(false)}
            onConfirm={handleConfirm}
          />
        </Content>
      </Container>
    </CommonTemplate>
  );
};
